import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { User, Session } from '@supabase/supabase-js';
import {
  supabaseAuth,
  profilesService,
  safeGetUser,
  invalidateProfileCache,
  invalidateUserUsageCache,
  invalidateCategoriesCache,
} from '../services/supabase';
import { hasOAuthExchangePendingInUrl } from '../utils/authRedirect';
import {
  clearLoggingOutFlag,
  isInvalidRefreshTokenError,
  isLoggingOutFlagSet,
  purgeSupabaseAuthStorage,
  safeLocalSignOut,
  setLoggingOutFlag,
} from '../utils/authReset';
import { isEmailAlreadyRegisteredError } from '../utils/authErrors';
import { clearAuthSnapshot, setAuthSnapshot, getAuthSnapshot, syncAccessToken, AUTH_SESSION_LOST_EVENT, isAuthSessionLostError } from '../services/authStore';
import { isAccessTokenExpired } from '../utils/jwt';
import { detectBrowserLanguage } from './I18nContext';
import { trackUmamiEvent } from '../utils/umami';
import { trackMetaCompleteRegistration } from '../utils/metaPixel';
import { trackOpenAICompleteRegistration } from '../utils/openaiPixel';

const GOOGLE_REG_TRACKED_PREFIX = 'leadsme:google_reg_tracked:';
const FRESH_AUTH_USER_MAX_AGE_MS = 5 * 60 * 1000;

/** True for Google OAuth users (primary or linked identity). */
function isGoogleProviderUser(authUser: User): boolean {
  if (authUser.app_metadata?.provider === 'google') return true;
  return (authUser.identities ?? []).some((identity) => identity.provider === 'google');
}

/** Auth user created moments ago (covers /login and /signup Google first-time). */
function isFreshAuthUser(authUser: User, maxAgeMs = FRESH_AUTH_USER_MAX_AGE_MS): boolean {
  const createdMs = authUser.created_at ? Date.parse(authUser.created_at) : NaN;
  if (!Number.isFinite(createdMs)) return false;
  const ageMs = Date.now() - createdMs;
  return ageMs >= 0 && ageMs < maxAgeMs;
}

/**
 * Fire CompleteRegistration once for a brand-new Google account.
 * Uses created_at (not which page started OAuth) so /login → Google signup is covered.
 */
function consumeFreshGoogleRegistration(authUser: User): boolean {
  if (!isGoogleProviderUser(authUser) || !isFreshAuthUser(authUser)) return false;
  try {
    const key = `${GOOGLE_REG_TRACKED_PREFIX}${authUser.id}`;
    if (sessionStorage.getItem(key)) return false;
    sessionStorage.setItem(key, '1');
    return true;
  } catch {
    return true;
  }
}

function redirectToLoginForExpiredSession(): void {
  if (typeof window === 'undefined') return;
  const path = window.location.pathname;
  if (!path.startsWith('/app')) return;
  if (isLoggingOutFlagSet()) return;
  const redirect = encodeURIComponent(`${path}${window.location.search}`);
  window.location.replace(`/login?redirect=${redirect}`);
}
interface AuthContextType {
  user: User | null;
  session: Session | null;
  loading: boolean;
  signUp: (email: string, password: string, userData: { name: string; company?: string }) => Promise<{ error: any }>;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signInWithGoogle: () => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  updateProfile: (updates: { name?: string; company?: string }) => Promise<{ error: any }>;
  sendOtp: (email: string, userData?: { name: string; company?: string }, otpType?: 'signup' | 'recovery') => Promise<{ error: any }>;
  verifyOtp: (email: string, token: string, type?: 'signup' | 'email' | 'recovery') => Promise<{ error: any }>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const initializedRef = useRef(false);
  const userRef = useRef<User | null>(null);
  const userCheckIntervalRef = useRef<NodeJS.Timeout | null>(null);
  /** After SIGNED_IN, don't treat missing profile as "account deleted" while bootstrap may still run. */
  const profileBootstrapGraceUntilRef = useRef(0);

  // Keep ref in sync with state
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  useEffect(() => {

    const hardResetAuth = async () => {
      // Clear client storage first (sync), then best-effort local sign out
      purgeSupabaseAuthStorage();
      try {
        await safeLocalSignOut();
      } catch {
        // ignore
      }
      clearAuthSnapshot();
      invalidateProfileCache();
      invalidateUserUsageCache();
      invalidateCategoriesCache();
      setSession(null);
      setUser(null);
      setLoading(false);
    };

    // Standard SaaS: Periodic check to verify user still exists in database
    // This handles cases where user is deleted from database but session is still valid
    const ensureProfileForUser = async (authUser: User): Promise<boolean> => {
      for (let attempt = 0; attempt < 3; attempt++) {
        try {
          const existing = await profilesService.getProfile({ force: true });
          if (existing) return true;
          const userMetadata = authUser.user_metadata || {};
          const browserLanguage = detectBrowserLanguage();
          await profilesService.createProfile({
            name:
              userMetadata.name ||
              userMetadata.full_name ||
              authUser.email?.split('@')[0] ||
              'User',
            company: userMetadata.company,
            language: browserLanguage,
          });
          const created = await profilesService.getProfile({ force: true });
          if (created) return true;
        } catch (err) {
          console.error('Error ensuring profile:', err);
        }
        await new Promise((r) => setTimeout(r, 400 * (attempt + 1)));
      }
      return false;
    };

    const checkUserExists = async (userId: string): Promise<boolean | 'session_lost'> => {
      try {
        const sync = await syncAccessToken();
        if (sync.status === 'logged_out') {
          return 'session_lost';
        }
        // Force: must not use cached profile when verifying account still exists
        let profile = await profilesService.getProfile({ force: true });
        // Auth died mid-check (anon RLS → null) — not the same as account deleted
        if (!getAuthSnapshot().user) {
          return 'session_lost';
        }
        if (profile) return true;

        // Missing profile shortly after sign-in: retry bootstrap, never force-delete yet.
        if (Date.now() < profileBootstrapGraceUntilRef.current) {
          const snapUser = getAuthSnapshot().user;
          if (snapUser?.id === userId) {
            await ensureProfileForUser(snapUser);
            profile = await profilesService.getProfile({ force: true });
            if (profile) return true;
          }
          return true;
        }

        return false;
      } catch (error) {
        if (isAuthSessionLostError(error)) {
          return 'session_lost';
        }
        // On error, assume user still exists to avoid false positives
        // (network errors, etc. shouldn't log out the user)
        console.error('Error checking if user exists:', error);
        return true;
      }
    };

    const handleUserDeleted = async () => {
      // User account no longer exists, signing out
      // Log only in development for debugging
      if (process.env.NODE_ENV !== 'production') {
        console.warn('[Auth] User account no longer exists, signing out');
      }
      // Clear interval before signing out
      if (userCheckIntervalRef.current) {
        clearInterval(userCheckIntervalRef.current);
        userCheckIntervalRef.current = null;
      }
      await hardResetAuth();
      // Redirect to login page
      window.location.href = '/login';
    };

    const startUserCheckInterval = () => {
      // Clear existing interval if any
      if (userCheckIntervalRef.current) {
        clearInterval(userCheckIntervalRef.current);
      }
      
      // Token sync often + existence check (standard SaaS: don't stay zombie after idle)
      userCheckIntervalRef.current = setInterval(async () => {
        const currentUser = userRef.current;
        if (!currentUser?.id) return;

        try {
          const sync = await syncAccessToken();
          if (sync.status === 'logged_out') {
            await hardResetAuth();
            redirectToLoginForExpiredSession();
            return;
          }
          if (sync.status === 'ok') {
            const snap = getAuthSnapshot();
            if (snap.session?.user) {
              setSession(snap.session);
              setUser(snap.user);
            }
          }
        } catch {
          const token = getAuthSnapshot().session?.access_token;
          if (!token || isAccessTokenExpired(token)) {
            await hardResetAuth();
            redirectToLoginForExpiredSession();
            return;
          }
        }

        const exists = await checkUserExists(currentUser.id);
        if (exists === 'session_lost') {
          await hardResetAuth();
          redirectToLoginForExpiredSession();
          return;
        }
        if (exists === false) {
          await handleUserDeleted();
        }
      }, 2 * 60 * 1000); // 2 minutes — catch expired JWT without waiting for tab focus
    };

    // Get initial session (only once on mount)
    const initializeAuth = async () => {
      // Prevent multiple initializations
      if (initializedRef.current) {
        return;
      }
      
      try {
        initializedRef.current = true;
        
        // STANDARD SaaS: Check authStore FIRST (synchronous) - fastest path
        // This allows public pages to render immediately if no cached session exists
        const snapshot = getAuthSnapshot();
        
        if (snapshot.session && snapshot.user) {
          // Fast path: use cached session from authStore (synchronous)
          setSession(snapshot.session);
          setUser(snapshot.user);
          setLoading(false);
          // Start periodic check for existing session in background
          if (snapshot.user?.id && !userCheckIntervalRef.current) {
            setTimeout(() => startUserCheckInterval(), 1000);
          }
          // Validate in background (non-blocking)
          setTimeout(async () => {
            try {
              const { data: { user: validatedUser }, error: userError } = await Promise.race([
                supabaseAuth.auth.getUser(),
                new Promise<{ data: { user: User | null }; error: any }>((resolve) =>
                  setTimeout(() => resolve({ data: { user: null }, error: { message: 'TIMEOUT' } }), 3000)
                ),
              ]);
              
              if (userError && isInvalidRefreshTokenError(userError)) {
                await hardResetAuth();
                return;
              }
              
              // Update if validation succeeded, otherwise keep cached session
              if (validatedUser && !userError) {
                setUser(validatedUser);
                setAuthSnapshot({ session: snapshot.session, user: validatedUser });
              }
            } catch {
              // Silent: keep cached session on validation error
            }
          }, 0);
          return;
        }
        
        // Check if user is already set (from onAuthStateChange that might have fired first)
        if (userRef.current) {
          setLoading(false);
          return;
        }

        const oauthCallback = hasOAuthExchangePendingInUrl();
        // PKCE exchange can take longer than a cold getSession; don't conclude "logged out" early.
        const sessionWaitMs = oauthCallback ? 10000 : 3000;
        const sessionPromise = supabaseAuth.auth.getSession();
        const sessionTimeoutPromise = new Promise<{ data: { session: Session | null } }>((resolve) =>
          setTimeout(() => resolve({ data: { session: null } }), sessionWaitMs)
        );
        let { data: { session } } = await Promise.race([sessionPromise as any, sessionTimeoutPromise]);

        // Double-check: if user was set while we were calling getSession, abort
        if (userRef.current) {
          setLoading(false);
          return;
        }

        // OAuth: first getSession may win the race before code exchange finishes — poll briefly.
        if (!session?.user && oauthCallback) {
          const { safeGetSession } = await import('../services/supabase');
          for (let i = 0; i < 20 && !userRef.current; i++) {
            await new Promise((r) => setTimeout(r, 250));
            if (userRef.current) {
              setLoading(false);
              return;
            }
            const { data } = await safeGetSession(2000);
            if (data.session?.user) {
              session = data.session;
              break;
            }
            if (!hasOAuthExchangePendingInUrl()) break;
          }
        }

        if (userRef.current) {
          setLoading(false);
          return;
        }

        if (session?.user) {
          // getSession() is storage-based; revalidate with getUser when possible.
          // On timeout / transient errors, keep the storage session (standard SaaS).
          const getUserPromise = supabaseAuth.auth.getUser();
          const userTimeoutPromise = new Promise<{ data: { user: User | null }; error: any }>((resolve) =>
            setTimeout(() => resolve({ data: { user: null }, error: { message: 'TIMEOUT' } }), 2500)
          );
          const { data: { user: validatedUser }, error: userError } = await Promise.race([
            getUserPromise as any,
            userTimeoutPromise,
          ]);

          if (userError && isInvalidRefreshTokenError(userError)) {
            await hardResetAuth();
            return;
          }

          // Prefer network-validated user; otherwise keep storage session (same as authStore fast path).
          const effectiveUser = validatedUser || session.user;
          setSession(session);
          setUser(effectiveUser);
          setAuthSnapshot({ session, user: effectiveUser });
          if (effectiveUser?.id && !userCheckIntervalRef.current) {
            setTimeout(() => startUserCheckInterval(), 1000);
          }
        } else {
          // IMPORTANT: Don't clear auth if user was already set by onAuthStateChange
          // (which can happen if user logs in while initializeAuth is still running)
          setUser((currentUser) => {
            if (currentUser) {
              return currentUser;
            }
            // Only clear snapshot if we're actually clearing the user
            clearAuthSnapshot();
            return null;
          });
          setSession((currentSession) => {
            if (currentSession) {
              return currentSession;
            }
            return null;
          });
        }

        setLoading(false);
      } catch (error) {
        if (isInvalidRefreshTokenError(error)) {
          await hardResetAuth();
          return;
        }
        console.error('[AuthContext] initializeAuth: Error getting initial session:', error);
        setSession(null);
        setUser(null);
        clearAuthSnapshot();
        setLoading(false);
      }
    };

    const applySnapshotToState = () => {
      const snap = getAuthSnapshot();
      if (snap.session?.user) {
        setSession(snap.session);
        setUser(snap.user);
      }
    };

    // Standard SaaS: re-sync session when user returns to a backgrounded tab / network.
    let resumeSyncInFlight = false;
    const syncSessionOnResume = async () => {
      if (document.visibilityState !== 'visible') return;
      if (!userRef.current || resumeSyncInFlight) return;

      resumeSyncInFlight = true;
      try {
        const result = await syncAccessToken();
        if (result.status === 'logged_out') {
          await hardResetAuth();
          redirectToLoginForExpiredSession();
          return;
        }
        if (result.status === 'ok') {
          applySnapshotToState();
        }
      } catch {
        // Keep current session on transient errors only if access token still usable.
        const token = getAuthSnapshot().session?.access_token;
        if (!token || isAccessTokenExpired(token)) {
          await hardResetAuth();
          redirectToLoginForExpiredSession();
        }
      } finally {
        resumeSyncInFlight = false;
      }
    };

    const handleVisibilityChange = () => {
      void syncSessionOnResume();
    };

    const handleWindowFocus = () => {
      void syncSessionOnResume();
    };

    const handleOnline = () => {
      void syncSessionOnResume();
    };

    // Helper to check if logout is in progress (avoid re-renders during hard navigation)
    const isLoggingOut = (): boolean => isLoggingOutFlagSet();

    const handleSessionLost = () => {
      if (isLoggingOut()) return;
      void (async () => {
        await hardResetAuth();
        redirectToLoginForExpiredSession();
      })();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);
    window.addEventListener('online', handleOnline);
    window.addEventListener(AUTH_SESSION_LOST_EVENT, handleSessionLost);

    // Subscribe BEFORE initializeAuth so PKCE / OAuth SIGNED_IN is never missed.
    // IMPORTANT: keep this callback synchronous. Awaiting auth/storage APIs here can deadlock
    // GoTrue's lock during PKCE code exchange (Google OAuth → bounce back to /login).
    const {
      data: { subscription },
    } = supabaseAuth.auth.onAuthStateChange((event, session) => {
      // Standard SaaS: Don't update React state during logout (hard navigation is about to happen)
      // This prevents unnecessary re-renders that would cause flashing
      if (isLoggingOut()) {
        return;
      }

      // Refresh failed: keep UI only if the access token is still usable; never restore a dead JWT.
      if (String(event) === 'TOKEN_REFRESH_FAILED') {
        setTimeout(() => {
          void (async () => {
            if (isLoggingOut()) return;
            try {
              const { data: { session: storedSession } } = await supabaseAuth.auth.getSession();
              const token = storedSession?.access_token;
              if (storedSession?.user && token && !isAccessTokenExpired(token)) {
                setSession(storedSession);
                setUser(storedSession.user);
                setAuthSnapshot({ session: storedSession, user: storedSession.user });
              } else {
                await hardResetAuth();
                redirectToLoginForExpiredSession();
              }
            } catch {
              await hardResetAuth();
              redirectToLoginForExpiredSession();
            } finally {
              setLoading(false);
            }
          })();
        }, 0);
        return;
      }

      // IMPORTANT: Always update state based on the session from the event
      // Don't try to be "smart" and skip updates - Supabase events are the source of truth
      if (session?.user) {
        setSession(session);
        setUser(session.user);
        setAuthSnapshot({ session, user: session.user });
        startUserCheckInterval();
        setLoading(false);

        // Defer DB checks / profile bootstrap so we never hold the GoTrue lock.
        if (event === 'TOKEN_REFRESHED') {
          const userId = session.user.id;
          setTimeout(() => {
            void (async () => {
              if (isLoggingOut()) return;
              try {
                const exists = await checkUserExists(userId);
                if (exists === 'session_lost') {
                  await hardResetAuth();
                  redirectToLoginForExpiredSession();
                  return;
                }
                if (exists === false) {
                  await handleUserDeleted();
                }
              } catch (error) {
                console.error('Error checking user existence on token refresh:', error);
              }
            })();
          }, 0);
        }

        if (event === 'SIGNED_IN') {
          const signedInSession = session;
          profileBootstrapGraceUntilRef.current = Date.now() + 5 * 60 * 1000;
          setTimeout(() => {
            void (async () => {
              if (isLoggingOut()) return;
              if (consumeFreshGoogleRegistration(signedInSession.user)) {
                trackUmamiEvent('signup_completed', { method: 'google' });
                trackMetaCompleteRegistration('google');
                trackOpenAICompleteRegistration('google');
              }
              try {
                await ensureProfileForUser(signedInSession.user);
              } catch (profileError) {
                console.error('Error creating profile on sign-in:', profileError);
              }
            })();
          }, 0);
        }
        return;
      }

      // IMPORTANT: Only clear if this is a SIGNED_OUT event.
      // Don't clear on other events that might have null session temporarily.
      if (event === 'SIGNED_OUT') {
        if (userCheckIntervalRef.current) {
          clearInterval(userCheckIntervalRef.current);
          userCheckIntervalRef.current = null;
        }
        setSession(null);
        setUser(null);
        clearAuthSnapshot();
        setLoading(false);
      }
    });

    initializeAuth();

    return () => {
      subscription.unsubscribe();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
      window.removeEventListener('online', handleOnline);
      window.removeEventListener(AUTH_SESSION_LOST_EVENT, handleSessionLost);
      // Clean up periodic check on unmount
      if (userCheckIntervalRef.current) {
        clearInterval(userCheckIntervalRef.current);
        userCheckIntervalRef.current = null;
      }
    };
  }, []);

  const signUp = async (email: string, password: string, userData: { name: string; company?: string }) => {
    try {
      // Use signUp with password - Supabase will send OTP if configured in dashboard
      // IMPORTANT: In Supabase dashboard, set Authentication > Email Templates > Confirm signup to "OTP" (not "Magic Link")
      // This ensures users receive a 6-digit code instead of a magic link
      const { error } = await supabaseAuth.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.name,
            company: userData.company,
          },
          // Don't set emailRedirectTo when using OTP - it's only for magic links
          // emailRedirectTo: `${window.location.origin}/app`,
        },
      });

      if (error) {
        console.error('Signup error:', error);

        if (isEmailAlreadyRegisteredError(error)) {
          return {
            error: {
              ...error,
              code: 'email_already_exists',
            },
          };
        }

        return { error };
      }

      // Supabase security: If email exists and confirmation is enabled, 
      // Supabase returns NO ERROR and NO OTP is sent (prevents email enumeration)
      // We always show OTP screen - user can try login if no code received

      return { error: null };
    } catch (error) {
      console.error('Signup exception:', error);
      return { error };
    }
  };

  const signIn = async (email: string, password: string) => {
    try {
      // A stuck logout guard ignores auth events and can bounce a fresh session.
      clearLoggingOutFlag();

      const { data, error } = await supabaseAuth.auth.signInWithPassword({
        email,
        password,
      });

      if (error) {
        console.error('[AuthContext] Signin error:', error);
        return { error };
      }

      // IMPORTANT: onAuthStateChange devrait se déclencher automatiquement,
      // mais on force une vérification immédiate pour éviter les délais
      if (data.session) {
        clearLoggingOutFlag();
        setSession(data.session);
        setUser(data.user);
        setAuthSnapshot({ session: data.session, user: data.user });
        setLoading(false);
      }

      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Signin exception:', error);
      return { error };
    }
  };

  const signInWithGoogle = async () => {
    try {
      clearLoggingOutFlag();
      const { error } = await supabaseAuth.auth.signInWithOAuth({
        provider: 'google',
        options: {
          redirectTo: `${window.location.origin}/app`,
        },
      });
      if (error) {
        console.error('[AuthContext] Google sign-in error:', error);
        return { error };
      }
      return { error: null };
    } catch (error) {
      console.error('[AuthContext] Google sign-in exception:', error);
      return { error };
    }
  };

  const signOut = async () => {
    try {
      // Always clear local state/storage so the user is effectively logged out.
      // Revoke server-side first (short timeout) so we don't leave a live refresh token,
      // then hard-navigate. Completing revoke before nav avoids killing a fresh re-login.
      setLoggingOutFlag();
      try {
        const globalSignOut = supabaseAuth.auth.signOut({ scope: 'global' } as any);
        const timeoutPromise = new Promise<{ error: null }>((resolve) =>
          setTimeout(() => resolve({ error: null }), 1500)
        );
        await Promise.race([globalSignOut as any, timeoutPromise]);
      } catch {
        // ignore — local purge below still logs the device out
      }
      try {
        purgeSupabaseAuthStorage();
      } catch {
        // ignore
      }
      clearAuthSnapshot();
      invalidateProfileCache();
      invalidateUserUsageCache();
      invalidateCategoriesCache();

      // UX: always go back to landing after logout (hard nav to reset state)
      window.location.replace('/');

      // Best-effort local cleanup if global timed out mid-flight
      try {
        const localPromise = safeLocalSignOut();
        const localTimeout = new Promise<void>((resolve) => setTimeout(resolve, 1000));
        void Promise.race([localPromise, localTimeout]).catch(() => {});
      } catch {
        // ignore
      }
    } catch (error) {
      console.error('[AuthContext] signOut: exception:', error);
      // Even on exception, force local logout + navigate
      setLoggingOutFlag();
      try {
        purgeSupabaseAuthStorage();
      } catch {
        // ignore
      }
      clearAuthSnapshot();
      window.location.replace('/');
    }
  };

  const resetPassword = async (email: string) => {
    try {
      // Use current origin: localhost in dev, production domain in prod
      const { error } = await supabaseAuth.auth.resetPasswordForEmail(email, {
        redirectTo: `${window.location.origin}/reset-password`,
      });

      if (error) {
        console.error('Reset password error:', error);
        return { error };
      }
      return { error: null };
    } catch (error) {
      console.error('Reset password exception:', error);
      return { error };
    }
  };

  const updateProfile = async (updates: { name?: string; company?: string }) => {
    try {
      if (!user) {
        return { error: 'No user logged in' };
      }

      // Update auth metadata
      const { error: authError } = await supabaseAuth.auth.updateUser({
        data: {
          name: updates.name,
          company: updates.company,
        },
      });

      if (authError) {
        return { error: authError };
      }

      // Refresh user state
      const { data: { user: updatedUser }, error: userError } = await safeGetUser(4000);
      if (!userError && updatedUser) {
        setUser(updatedUser);
      }

      return { error: null };
    } catch (error) {
      return { error };
    }
  };


  const sendOtp = async (email: string, userData?: { name: string; company?: string }, otpType: 'signup' | 'recovery' = 'signup') => {
    try {
      if (otpType === 'recovery') {
        // For password recovery, use resetPasswordForEmail with OTP
        // Supabase will send OTP if configured in dashboard
        const { error } = await supabaseAuth.auth.resetPasswordForEmail(email, {
          // Don't set redirectTo - we're using OTP, not magic link
        });
        if (error) {
          console.error('Send recovery OTP error:', error);
          // Handle rate limiting explicitly for recovery as well
          const lowerMsg = (error.message || '').toLowerCase();
          const isRateLimited = 
            (error as any)?.status === 429 ||
            lowerMsg.includes('too many requests') ||
            lowerMsg.includes('you can only request this after') ||
            lowerMsg.includes('request rate limit');
          if (isRateLimited) {
            const waitTimeMatch = (error.message || '').match(/(\d+)\s*seconds?/i);
            const waitSeconds = waitTimeMatch ? parseInt(waitTimeMatch[1], 10) : 60;
            return {
              error: {
                ...error,
                isRateLimited: true,
                waitSeconds,
              },
            };
          }
          return { error };
        }
        return { error: null };
      }
      
      // For OTP resend after signup, we use signInWithOtp
      // Note: This sends an OTP of type 'email', but verifyOtp will use type 'signup'
      // This is the standard Supabase approach for OTP resend
      const { error } = await supabaseAuth.auth.signInWithOtp({
        email,
        options: {
          data: userData ? {
            name: userData.name,
            company: userData.company,
          } : undefined,
          // Don't set emailRedirectTo for OTP - it's only for magic links
        },
      });

      if (error) {
        console.error('Send OTP error:', error);
        
        // Check for rate limiting
        const lowerMsg = (error.message || '').toLowerCase();
        const isRateLimited = 
          error.status === 429 || 
          lowerMsg.includes('too many requests') ||
          lowerMsg.includes('you can only request this after') ||
          lowerMsg.includes('request rate limit');
        
        if (isRateLimited) {
          const waitTimeMatch = (error.message || '').match(/(\d+)\s*seconds?/i);
          const waitSeconds = waitTimeMatch ? parseInt(waitTimeMatch[1], 10) : 60;
          
          return { 
            error: {
              ...error,
              isRateLimited: true,
              waitSeconds,
            }
          };
        }
        
        return { error };
      }

      return { error: null };
    } catch (error) {
      console.error('Send OTP exception:', error);
      return { error };
    }
  };

  const verifyOtp = async (email: string, token: string, type: 'signup' | 'email' | 'recovery' = 'signup') => {
    try {
      clearLoggingOutFlag();
      const { data, error } = await supabaseAuth.auth.verifyOtp({
        email,
        token,
        type,
      });

      if (error) {
        console.error('Verify OTP error:', error);
        return { error };
      }

      // If verification successful, session will be set by onAuthStateChange
      // But we can also set it immediately for better UX
      if (data.session && data.user) {
        clearLoggingOutFlag();
        setSession(data.session);
        setUser(data.user);
        setAuthSnapshot({ session: data.session, user: data.user });
        setLoading(false);
      }

      return { error: null };
    } catch (error) {
      console.error('Verify OTP exception:', error);
      return { error };
    }
  };

  const value: AuthContextType = {
    user,
    session,
    loading,
    signUp,
    signIn,
    signInWithGoogle,
    signOut,
    resetPassword,
    updateProfile,
    sendOtp,
    verifyOtp,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};